import { X, Sparkles, Target, CheckCircle2, DollarSign, Award, Activity, ExternalLink, Bot } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  job: any | null;
  ai: any | null;
  onAnalyze?: (() => void) | undefined;
  analyzing?: boolean | undefined;
}

const decisionPill = (d?: string) => {
  const map: Record<string, string> = {
    Accept: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    Reject: "bg-red-50 text-red-700 ring-red-200",
    Maybe:  "bg-amber-50 text-amber-700 ring-amber-200",
  };
  return map[d ?? ""] || "bg-slate-100 text-slate-600 ring-slate-200";
};

const Block = ({ icon: Icon, title, text, accent }: any) => {
  const accents: Record<string, string> = {
    violet: "border-violet-200 bg-violet-50/40",
    blue: "border-blue-200 bg-blue-50/40",
    emerald: "border-emerald-200 bg-emerald-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    rose: "border-rose-200 bg-rose-50/40",
  };
  if (!text) return null;
  return (
    <div className={`p-4 rounded-xl border ${accents[accent]}`}>
      <div className="flex items-center gap-2 mb-2 text-slate-700">
        <Icon size={14} />
        <h4 className="text-xs font-bold uppercase tracking-wider">{title}</h4>
      </div>
      <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{text}</p>
    </div>
  );
};

const JobAnalysisModal = ({ open, onClose, job, ai, onAnalyze, analyzing }: Props) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>

        <div className="flex items-start justify-between gap-4 p-6 border-b border-slate-100">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 text-xs text-slate-500">
              <Sparkles size={12} className="text-violet-500" />
              AI Job Analysis
              {job?.["Job ID"] && <span className="text-slate-400">• ID: {job["Job ID"]}</span>}
            </div>
            <h2 className="text-lg font-bold text-slate-900 leading-snug">
              {job?.["Job Title"] || "Untitled job"}
            </h2>
            {job && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {ai?.["Final Decision"] && (
                  <span className={`px-2.5 py-1 text-xs font-bold rounded-lg ring-1 ${decisionPill(ai["Final Decision"])}`}>
                    {ai["Final Decision"]}
                  </span>
                )}
                <span className="px-2.5 py-1 text-xs font-bold rounded-lg ring-1 bg-blue-50 text-blue-700 ring-blue-200">
                  {job["Budget Amount"] || "—"}
                </span>
                <span className="px-2.5 py-1 text-xs font-bold rounded-lg ring-1 bg-slate-50 text-slate-700 ring-slate-200">
                  {job["Contract Type"] || "—"}
                </span>
                <span className="px-2.5 py-1 text-xs font-medium rounded-lg ring-1 bg-slate-50 text-slate-600 ring-slate-200">
                  {job["Country"] || "—"} • {job["Total Applicants"] ?? 0} apps
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {job?.["URL"] && (
              <a href={job["URL"]} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#03488A] to-[#00AAF1] text-white hover:opacity-95">
                <ExternalLink size={13} /> Upwork
              </a>
            )}
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-6 space-y-4">
          {analyzing && !job ? (
            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200 text-center">
              <Bot size={32} className="text-violet-500 mx-auto mb-2 animate-pulse" />
              <p className="text-sm font-semibold text-slate-700 mb-1">Analyzing with AI…</p>
              <p className="text-xs text-slate-500">This may take 30–90 seconds. Please wait.</p>
            </div>
          ) : !ai || !ai["Final Decision"] ? (
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center">
              <Bot size={32} className="text-slate-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-700 mb-1">No AI analysis yet</p>
              <p className="text-xs text-slate-500 mb-4">
                This job hasn't been analyzed. Trigger AI to scrape and evaluate it.
              </p>
              {onAnalyze && (
                <button
                  onClick={onAnalyze}
                  disabled={analyzing}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                >
                  <Sparkles size={14} /> {analyzing ? "Queuing..." : "Analyze Now"}
                </button>
              )}
            </div>
          ) : (
            <>
              <Block icon={Target}        title="Final Synthesis"          text={ai["Final Synthesis"]}              accent="violet" />
              <Block icon={CheckCircle2}  title={`Hard Filter — ${ai["Hard Filter Decision"] || ""}`} text={ai["Hard Filter Reasoning"]} accent="blue" />
              <Block icon={DollarSign}    title="Budget Guardrail"          text={ai["Budget Guardrail Reasoning"]}   accent="emerald" />
              <Block icon={Award}         title={`Weighted Score${ai["Weighted Scoring Score"] ? ` — ${ai["Weighted Scoring Score"]}` : ""}`} text={ai["Weighted Scoring Reasoning"]} accent="amber" />
              <Block icon={Activity}      title={`EV Calculator${ai["EV Calculator Decision"] ? ` — ${ai["EV Calculator Decision"]}` : ""}`} text={ai["EV Calculator Reasoning"]} accent="rose" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobAnalysisModal;
